'use client';

import { HTMLProps, useState, useEffect, useRef } from 'react';
import { Image as ImageIcon, Loader2, ExternalLink } from 'lucide-react';
import { cn } from '../../lib/shadcn/utils';
import { ImageSourceEnum, Image as ImageType } from '@city-sage/core-client';


export interface ImageProps extends HTMLProps<HTMLImageElement> {
  className?: string;
  imageClassName?: string;
  image?: ImageType;
  priority?: boolean; // For critical images that should load immediately
  lazy?: boolean; // Control lazy loading behavior
  sizes?: string; // Responsive image sizes
  quality?: number; // Image quality (0-100)
}

export function Image({ 
  className = '', 
  imageClassName = '', 
  image, 
  priority = false,
  lazy = true,
  sizes,
  quality = 85,
  ...rest 
}: ImageProps) {
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isIntersecting, setIsIntersecting] = useState(!lazy || priority);
  const imgRef = useRef<HTMLImageElement>(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!lazy || priority || isIntersecting) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsIntersecting(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '50px', // Start loading 50px before image enters viewport
        threshold: 0.1,
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [lazy, priority, isIntersecting]);

  const handleError = () => {
    setError(true);
    setLoading(false);
  };

  const handleLoad = () => {
    setLoading(false);
  };

  // Use image.url if src is not provided in rest props
  const src = rest.src || image?.url;
  
  // Only load image if it's intersecting (for lazy loading) or priority
  const shouldLoad = isIntersecting || priority;
  
  // Check if we should show attribution
  const shouldShowAttribution = image?.source === ImageSourceEnum.Unsplash && 
    (image?.authorName || image?.photographerUsername);
  
  const authorName = (image?.authorName || image?.photographerUsername);
  const authorUrl = image?.authorUrl;
  
  // Build UTM parameters for Unsplash attribution
  const utmParams = '?utm_source=CitySage&utm_medium=referral';
  const unsplashUrl = `https://unsplash.com/${utmParams}`;
  const photographerUrl = authorUrl ? `${authorUrl}${utmParams}` : undefined;

  return (
    <div className={cn('relative', className)}>
      {error || !src || src === "" ? (
        <div className="flex items-center justify-center w-full h-full bg-gray-200">
          <ImageIcon className="w-1/4 h-1/4 text-gray-400" />
        </div>
      ) : (
        <>
          {loading && shouldLoad && (
            <div className="absolute inset-0 bg-black flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
            </div>
          )}
          <img 
            {...rest}
            ref={imgRef}
            src={shouldLoad ? src : undefined}
            onError={handleError}
            onLoad={handleLoad}
            loading={priority ? 'eager' : 'lazy'}
            sizes={sizes}
            className={cn(
              'w-full h-full object-contain bg-black transition-opacity duration-300',
              loading && shouldLoad ? 'opacity-0' : 'opacity-100',
              imageClassName
            )} 
          />
          
          {/* Attribution overlay for Unsplash images */}
          {shouldShowAttribution && !loading && !error && shouldLoad && (
            <div className="absolute bottom-0.5 right-0.5 bg-black/80 backdrop-blur-sm text-white rounded text-[10px] leading-none px-1.5 py-1 max-w-[85%]">
              <div className="flex flex-col items-start gap-0.5 min-w-0">
                {photographerUrl ? (
                  <a
                    href={photographerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white hover:text-gray-300 transition-colors flex items-center gap-1 min-w-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span className="truncate">{authorName}</span>
                  </a>
                ) : (
                  <span className="truncate">{authorName}</span>
                )}
                <a
                  href={unsplashUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-gray-300 transition-colors flex items-center gap-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span>Unsplash</span>
                </a>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
